import axiosInstance from '../AxiosClient'
import {
  createApi,
  createCollectionManager,
  createCustomServiceCall,
  Pagination,
} from '@thinknimble/tn-models'
import {
  workedHoursShape,
  updatedWorkedHourShape,
  customRetrieveWorkedHourInputShape,
  rangeRetrieveInputShape,
  customRetrieveAllWorkedHourInputShape,
  rangeRetrieveAllInputShape,
  canvassingOrgAdminChartInputShape,
  deleteWorkedHourInputShape,
  storeFundraiserStatsTemplateShape,
  removeFundraiserStatsTemplateShape,
  exportWorkedHoursTemplateShape,
  stopCountRetrieveInputShape,
  stopCountUpdateInputShape,
} from './models'

const DAILY_WORKED_HOURS_ENDPOINT = '/daily-work-hours/'
const FILTER_TEMPLATES_ENDPOINT = '/filterTemplates/'
import fileDownload from 'js-file-download'

const retrieve = createCustomServiceCall(
  {
    inputShape: customRetrieveWorkedHourInputShape,
    outputShape: workedHoursShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { campaign, date } = toApi(input)
    const response = await client.get(`${DAILY_WORKED_HOURS_ENDPOINT}`, {
      params: {
        campaign,
        date,
      },
    })
    return response.data
  },
)

const deleteWorkingHour = createCustomServiceCall(
  {
    inputShape: deleteWorkedHourInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { id, ...data } = toApi(input)
    await client.delete(`${DAILY_WORKED_HOURS_ENDPOINT}${id}/`, data)
  },
)

const customRetrieve = createCustomServiceCall(
  {
    inputShape: customRetrieveWorkedHourInputShape,
    outputShape: workedHoursShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    try {
      const { date, campaign } = toApi(input)
      const response = await client.get(`${DAILY_WORKED_HOURS_ENDPOINT}export_work_hours/`, {
        params: { date, campaign },
        responseType: 'blob',
      })
      fileDownload(response.data, 'Fundraiser_Stats_by_Day.csv')
      return response.data
    } catch (error) {
      console.error('Error downloading file:', error)
      throw error
    }
  },
)

const customRetrieveAll = createCustomServiceCall(
  {
    inputShape: customRetrieveAllWorkedHourInputShape,
    outputShape: workedHoursShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    try {
      const { date, canvassing_org, campaign, fundraiser, home_office } = toApi(input)
      const params = { date }
      if (canvassing_org) {
        params.canvassing_org = canvassing_org
      }
      if (campaign) {
        params.campaign = campaign
      }
      if (fundraiser) {
        params.fundraiser = fundraiser
      }
      if (home_office) {
        params.home_office = home_office
      }
      const response = await client.get(`${DAILY_WORKED_HOURS_ENDPOINT}export_work_hours/`, {
        params,
        responseType: 'blob',
      })
      fileDownload(response.data, 'Fundraiser_Stats_by_Day.csv')
    } catch (error) {
      console.error('Error downloading file:', error)
      throw error
    }
  },
)

const rangeRetrieve = createCustomServiceCall(
  {
    inputShape: rangeRetrieveInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    try {
      const { start_date, end_date, campaign } = toApi(input)
      const response = await client.get(`${DAILY_WORKED_HOURS_ENDPOINT}export_work_hours/`, {
        params: { start_date, end_date, campaign },
        responseType: 'blob',
      })
      fileDownload(response.data, 'Fundraiser_Stats_by_Day.csv')
    } catch (error) {
      console.error('Error downloading file:', error)
      throw error
    }
  },
)

const rangeRetrieveAll = createCustomServiceCall(
  {
    inputShape: rangeRetrieveAllInputShape,
    outputShape: workedHoursShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    try {
      const { start_date, end_date, canvassing_org, campaign, fundraiser, home_office } =
        toApi(input)
      const params = { start_date, end_date }
      if (canvassing_org) {
        params.canvassing_org = canvassing_org
      }
      if (campaign) {
        params.campaign = campaign
      }
      if (fundraiser) {
        params.fundraiser = fundraiser
      }
      if (home_office) {
        params.home_office = home_office
      }
      const response = await client.get(`${DAILY_WORKED_HOURS_ENDPOINT}export_work_hours/`, {
        params,
        responseType: 'blob',
      })
      fileDownload(response.data, 'Fundraiser_Stats_by_Day.csv')
    } catch (error) {
      console.error('Error downloading file:', error)
      throw error
    }
  },
)

const downloadCustomTemplate = createCustomServiceCall(
  {
    inputShape: exportWorkedHoursTemplateShape,
  },
  async ({ client, input, utils }) => {
    try {
      const formattedData = utils.toApi(input)
      const response = await client.post(
        `${DAILY_WORKED_HOURS_ENDPOINT}download_custom_template/`,
        formattedData,
        {
          responseType: 'blob',
        },
      )
      fileDownload(response.data, 'Fundraiser_Stats_by_Day.csv')
      return response.data
    } catch (error) {
      console.error('Error downloading file:', error)
      throw error
    }
  },
)

const saveFundraiserStatsTemplate = createCustomServiceCall(
  {
    inputShape: storeFundraiserStatsTemplateShape,
  },
  async ({ client, input, utils }) => {
    const formattedData = utils.toApi(input)
    const response = await client.post(`${FILTER_TEMPLATES_ENDPOINT}`, formattedData)
    return response.data
  },
)

const listFilterTemplates = createCustomServiceCall(async ({ client }) => {
  const res = await client.get(`${FILTER_TEMPLATES_ENDPOINT}`)
  return res.data
})

const removeFundraiserStatsTemplate = createCustomServiceCall(
  {
    inputShape: removeFundraiserStatsTemplateShape,
  },
  async ({ client, input, utils }) => {
    const { template_id } = utils.toApi(input)
    const params = `?id=${template_id}`
    const res = await client.delete(`${FILTER_TEMPLATES_ENDPOINT}delete_template/${params}`)
    return res.data
  },
)

const customUpdate = createCustomServiceCall(
  {
    inputShape: updatedWorkedHourShape,
    outputShape: workedHoursShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { id, ...rest } = toApi(input)
    await client.patch(`${DAILY_WORKED_HOURS_ENDPOINT}${id}/`, rest)
  },
)

const shifts_spent_per_campaign = createCustomServiceCall(
  {
    inputShape: canvassingOrgAdminChartInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { start_date, end_date, campaign_filter_value } = toApi(input)
    const params = `?start_date=${start_date}&end_date=${end_date}&campaign_list=${campaign_filter_value}`
    const res = await client.get(
      `${DAILY_WORKED_HOURS_ENDPOINT}shifts_spent_per_campaign/${params}`,
    )
    return res.data
  },
)

const shifts_spent_by_fundraisers = createCustomServiceCall(
  {
    inputShape: canvassingOrgAdminChartInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { start_date, end_date, campaign_filter_value, fundraiser_filter_value } = toApi(input)
    const params = `?start_date=${start_date}&end_date=${end_date}&campaign_list=${campaign_filter_value}&fundraiser_list=${fundraiser_filter_value}`
    const res = await client.get(
      `${DAILY_WORKED_HOURS_ENDPOINT}shifts_spent_by_fundraisers/${params}`,
    )
    return res.data
  },
)

const stops_by_fundraisers = createCustomServiceCall(
  {
    inputShape: canvassingOrgAdminChartInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { start_date, end_date, campaign_filter_value, fundraiser_filter_value } = toApi(input)
    const params = `?start_date=${start_date}&end_date=${end_date}&campaign_list=${campaign_filter_value}&fundraiser_list=${fundraiser_filter_value}`
    const res = await client.get(`${DAILY_WORKED_HOURS_ENDPOINT}stops_by_fundraisers/${params}`)
    return res.data
  },
)

const getStopCount = createCustomServiceCall(
  {
    inputShape: stopCountRetrieveInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const { campaign } = toApi(input)
    const res = await client.get(`${DAILY_WORKED_HOURS_ENDPOINT}stop_count/`, {
      params: { campaign },
    })
    return res.data
  },
)

const updateStopCount = createCustomServiceCall(
  {
    inputShape: stopCountUpdateInputShape,
  },
  async ({ client, input, utils: { toApi } }) => {
    const payload = toApi(input)
    const res = await client.post(`${DAILY_WORKED_HOURS_ENDPOINT}stop_count/`, payload)
    return res.data
  },
)

const getTotalStopCount = createCustomServiceCall(async ({ client }) => {
  const res = await client.get(`${DAILY_WORKED_HOURS_ENDPOINT}total_stop_count/`)
  return res.data
})

export const WorkedHoursApi = createApi(
  {
    baseUri: DAILY_WORKED_HOURS_ENDPOINT,
    client: axiosInstance,
    models: {
      entity: workedHoursShape,
    },
  },
  {
    customUpdate,
    customRetrieve,
    rangeRetrieve,
    customRetrieveAll,
    rangeRetrieveAll,
    shifts_spent_per_campaign,
    retrieve,
    shifts_spent_by_fundraisers,
    stops_by_fundraisers,
    deleteWorkingHour,
    saveFundraiserStatsTemplate,
    listFilterTemplates,
    removeFundraiserStatsTemplate,
    downloadCustomTemplate,
    getStopCount,
    updateStopCount,
    getTotalStopCount,
  },
)

export const workedHourFunctions = () => {
  const workedHourCollection = createCollectionManager({
    fetchList: WorkedHoursApi.list,
    pagination: new Pagination(),
  })

  return {
    workedHourCollection,
  }
}
